import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    price: 25,
    currency: "€",
    category: "",
    prompt_template: "",
    display_order: 0,
    is_active: true
  });

  const fetchProduct = async () => {
    try {
      const data = await adminApi.getProduct(id);
      setProduct(data);
      setForm({
        name: data.name,
        price: data.price,
        currency: data.currency,
        category: data.category,
        prompt_template: data.prompt_template || "",
        display_order: data.display_order,
        is_active: data.is_active
      });
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const data = await adminApi.getCategories();
      setCategories(data.data.filter(c => c.is_active));
    } catch (e) {
      console.error("Failed to load categories:", e);
    }
  };

  useEffect(() => {
    fetchProduct();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateProduct(id, {
        ...form,
        prompt_template: form.prompt_template || null
      });
      toast.success("Product updated");
      fetchProduct();
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      await adminApi.uploadProductImage(id, file);
      toast.success("Image uploaded");
      fetchProduct();
    } catch (e) {
      toast.error(e.message);
    }
    setUploading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImageDelete = async () => {
    if (!window.confirm("Delete this base image?")) return;
    try {
      await adminApi.deleteProductImage(id);
      toast.success("Image deleted");
      fetchProduct();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (!product) return <div className="text-center py-8">Product not found</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">{product.name}</h1>
          <p className="text-gray-500 font-mono">{product.slug}</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/admin/products")}>Back to Products</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Product Info</CardTitle>
            <CardDescription>Basic product settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label>Price</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm({...form, price: parseFloat(e.target.value)})} />
              </div>
              <div className="w-20">
                <Label>Currency</Label>
                <Input value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-gray-500">No categories available</div>
                  ) : (
                    categories.map((cat) => (
                      <SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Display Order</Label>
              <Input type="number" value={form.display_order} onChange={(e) => setForm({...form, display_order: parseInt(e.target.value)})} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({...form, is_active: v})} />
              <Label>Active</Label>
            </div>
          </CardContent>
        </Card>

        {/* Base Product Image */}
        <Card>
          <CardHeader>
            <CardTitle>Base Product Image</CardTitle>
            <CardDescription>
              The blank mockup image Gemini uses as foundation for generating branded versions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {product.base_image_id ? (
              <div className="space-y-3">
                <div className="relative rounded-lg overflow-hidden border bg-gray-50">
                  <img
                    src={`${API}/api/images/${product.base_image_id}`}
                    alt="Base product"
                    className="w-full h-48 object-contain"
                  />
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleImageDelete}
                  className="w-full"
                >
                  Remove Image
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="text-gray-400 mb-3">No base image uploaded</div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload Image"}
                </Button>
                <p className="text-xs text-gray-400 mt-2">PNG, JPG up to 5MB</p>
              </div>
            )}
            {product.base_image_id && (
              <div className="pt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-replace"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? "Uploading..." : "Replace Image"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prompt Template */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>Product-Specific Prompt</CardTitle>
            <CardDescription>
              Custom prompt template for this product. Leave empty to use the global "product_mockup_base" prompt.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600 mb-4">
              <strong>Available variables:</strong>
              <code className="block mt-1 text-xs">
                {"{brand_name}"} {"{design_instructions}"} {"{product_name}"} {"{primary_color}"} {"{secondary_color}"} {"{logo_instruction}"} {"{colors}"}
              </code>
            </div>
            <Textarea
              rows={16}
              value={form.prompt_template}
              onChange={(e) => setForm({...form, prompt_template: e.target.value})}
              placeholder="Leave empty to use the global product_mockup_base prompt..."
              className="font-mono text-sm"
            />
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1">
                {saving ? "Saving..." : "Save Changes"}
              </Button>
              <Button variant="outline" onClick={() => setForm({...form, prompt_template: ""})}>
                Clear (Use Default)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
